import { Fragment, useState } from "react";
import { ArrowRightLeft, Check, ChevronDown, ChevronRight, Trash2, Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  PRAZDNY_STAV,
  VyberPolozkyDialog,
  type FilterCennika,
  type StavPolozky,
} from "@/components/import/VyberPolozkyDialog";
import type { Predvyplnenie } from "@/components/cennik/MaterialEditDialog";
import type { MapovaniePolozka } from "@/api/hooks/useTopsolidRaw";
import {
  ZapamataneStitok,
  ZapamatatPrepinac,
  ZoZakazkyStitok,
  jeZapamatane,
  jeZoZakazky,
} from "@/components/import/Zapamatanie";
import { inaHrubkaRiadku, zmenaHrubky } from "@/lib/import/hrubkaMaterialu";
import { formatMm } from "@/lib/splitRules";
import { nazovZostavy, vetaKontextu, vyskytyPoZostavach } from "@/lib/import/vyskyty";

/**
 * Materiály, pracovné dosky a kovania na priradenie — jeden riadok
 * tabuľky na položku.
 *
 * Do KOLA 89 mal riadok tri poschodia (popis + ovládanie, štyri široké
 * návrhy, celoširoké hľadanie) — pri siedmich materiáloch ~1000 px
 * a nič nesedelo v stĺpcoch. Navyše na reálnych dátach návrhy často
 * nenájdu nič (`F001249`, `K682`), takže to, čo user reálne používa,
 * bolo až úplne dole pod nepoužiteľnými návrhmi.
 *
 * Teraz je riadok ~32 px a celý výber (návrhy + hľadanie + nenahrávať)
 * je za klikom na priradenie — rovnako, ako to už majú hrany. Zapamätať
 * je od KOLA 204 vlastný stĺpec s nápisom (`Zapamatanie.tsx`).
 */

export function klucPolozky(p: MapovaniePolozka): string {
  return `${p.druh}|${p.token}|${p.kontext}`;
}

/**
 * Je položka rozhodnutá? Auto-vybraná hrana sa priraďovať nemusí.
 *
 * Nemožná hrúbka (KOLO 216 — ohýbaný dielec bez Rozvinu, 100 mm) je
 * vyriešená len ako Nenahrávať; doska by jej bola vždy zlá. Rovnako to
 * stráži server (`chybajuce`).
 */
export function jeVyriesena(
  p: MapovaniePolozka,
  stav: StavPolozky | undefined,
): boolean {
  if (p.nemozna_hrubka) return Boolean(stav?.zmazat);
  if (p.auto_material_id) return true;
  return Boolean(stav && (stav.zmazat || stav.material_id !== null));
}

export function TabulkaPoloziek({
  nadpis,
  poznamka,
  polozky,
  stavy,
  nazvyVybranych,
  filterPre,
  predvyplneniePre,
  onZmena,
  onKovanie,
  onPrepniDosku,
  menaZostav,
}: {
  nadpis: string;
  poznamka?: string;
  polozky: MapovaniePolozka[];
  stavy: Record<string, StavPolozky>;
  /** Názvy ručne vybraných položiek — picker ich vracia pri výbere. */
  nazvyVybranych: Record<string, string>;
  filterPre: (p: MapovaniePolozka) => FilterCennika;
  /** Čím predvyplniť formulár, keď sa položka zakladá do cenníka. */
  predvyplneniePre?: (p: MapovaniePolozka) => Predvyplnenie;
  onZmena: (kluc: string, s: StavPolozky, nazov?: string) => void;
  /** „Toto nie je doska, je to kovanie" — len pri plošnom materiáli. */
  onKovanie?: (p: MapovaniePolozka) => void;
  /**
   * „Je to pracovná / plošná doska" (KOLO 168) — presun do druhého
   * zoznamu. Smer dáva riadok (`je_pracovna_doska`), handler je jeden.
   */
  onPrepniDosku?: (p: MapovaniePolozka) => void;
  /** Mená zostáv z Tandem Zostáv `{urceni: jmeno}` — k výskytom (KOLO 216). */
  menaZostav?: Record<string, string> | null;
}): JSX.Element | null {
  const [editovana, setEditovana] = useState<MapovaniePolozka | null>(null);
  /** Riadok s rozbaleným „Kde je" (KOLO 216) — jeden naraz. */
  const [rozbalena, setRozbalena] = useState<string | null>(null);

  if (!polozky.length) return null;

  const klucEditovanej = editovana ? klucPolozky(editovana) : null;

  return (
    <section className="overflow-hidden rounded-lg border">
      <h3 className="flex flex-wrap items-center gap-2 border-b bg-background-alt px-3 py-1.5 text-sm font-medium">
        {nadpis}
        <span className="text-xs font-normal text-muted-foreground">
          ({polozky.length})
        </span>
        {poznamka ? (
          <span className="text-xs font-normal text-muted-foreground">
            {poznamka}
          </span>
        ) : null}
      </h3>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[220px]">Položka</TableHead>
            <TableHead className="w-[80px] text-right">Výskytov</TableHead>
            <TableHead>Priradenie</TableHead>
            {/* Zapamätanie má vlastný stĺpec s nápisom — holá fajka medzi
                ikonami sa dala ťuknúť omylom (KOLO 204) */}
            <TableHead className="w-[128px]">Zapamätať</TableHead>
            {/* Prepínač pracovná/plošná doska je ikona navyše (KOLO 168) */}
            <TableHead className={onPrepniDosku ? "w-[90px]" : "w-[66px]"}>
              Akcie
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {polozky.map((p) => {
            const k = klucPolozky(p);
            const stav = stavy[k] ?? PRAZDNY_STAV;
            const zostavy = vyskytyPoZostavach(p, menaZostav);
            const otvorena = rozbalena === k;
            return (
              <Fragment key={k}>
                <TableRow
                  className={p.nemozna_hrubka && !stav.zmazat ? "bg-destructive/10" : undefined}
                >
                  <TableCell className="px-3 py-1.5 font-mono text-xs">
                    {p.popis}
                  </TableCell>
                  <TableCell className="px-3 py-1.5 text-right text-xs text-muted-foreground">
                    {/* Kde je táto hrúbka / toto kovanie (KOLO 216) */}
                    {zostavy.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => setRozbalena(otvorena ? null : k)}
                        aria-expanded={otvorena}
                        aria-label={`Kde je — ${p.popis}`}
                        title="Ukázať dielce a zostavy"
                        className="inline-flex items-center gap-0.5 hover:text-foreground hover:underline"
                      >
                        {otvorena ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                        {p.pocet_vyskytov}×
                      </button>
                    ) : (
                      <>{p.pocet_vyskytov}×</>
                    )}
                  </TableCell>
                  <TableCell className="max-w-0 px-3 py-1.5 text-xs">
                    {p.nemozna_hrubka ? (
                      // Taká doska nie je — výber z cenníka by len zakryl
                      // chybu v modeli (KOLO 216)
                      <span className="block whitespace-normal text-destructive">
                        {stav.zmazat
                          ? "nenahráva sa"
                          : "Taká hrúbka nemôže byť — ohýbaný dielec bez Rozvinu? Oprav v TopSolide, alebo Nenahrávať."}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditovana(p)}
                        aria-label={`Priradenie — ${p.popis}`}
                        title="Vybrať položku z cenníka"
                        className="flex w-full items-center gap-1.5 truncate text-left hover:underline"
                      >
                        <PriradenieText
                          polozka={p}
                          stav={stav}
                          nazov={nazvyVybranych[k]}
                        />
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="px-3 py-1.5">
                    <ZapamatatPrepinac
                      polozka={p}
                      stav={stav}
                      onZmena={(s) => onZmena(k, s)}
                    />
                  </TableCell>
                  <TableCell className="px-3 py-1.5">
                    {/* Ikony s `title`; celým textom sú tie isté voľby aj
                        vo výberovom dialógu, takže sa nedajú prehliadnuť */}
                    <div className="flex items-center gap-0.5">
                      {onPrepniDosku ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          aria-label={`${
                            p.je_pracovna_doska
                              ? "Je to plošná doska"
                              : "Je to pracovná doska"
                          } — ${p.popis}`}
                          title={
                            p.je_pracovna_doska
                              ? "Presunúť medzi plošné dosky (napr. škárovka 38 mm)"
                              : "Presunúť medzi pracovné dosky (kupujú sa hotové na bežné metre)"
                          }
                          onClick={() => onPrepniDosku(p)}
                        >
                          <ArrowRightLeft className="h-3.5 w-3.5" />
                        </Button>
                      ) : null}
                      {onKovanie ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          aria-label={`Je to kovanie — ${p.popis}`}
                          title="Presunúť medzi kovania (v TopSolide chýba ZPRACOVANI=hw)"
                          onClick={() => onKovanie(p)}
                        >
                          <Wrench className="h-3.5 w-3.5" />
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant={stav.zmazat ? "secondary" : "ghost"}
                        className="h-6 w-6 p-0"
                        aria-label={`Nenahrávať — ${p.popis}`}
                        title="Túto položku neimportovať"
                        onClick={() =>
                          onZmena(k, {
                            ...stav,
                            zmazat: !stav.zmazat,
                            material_id: null,
                          })
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      {jeVyriesena(p, stav) ? (
                        <Check className="ml-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      ) : (
                        <span className="ml-0.5 w-4 shrink-0" />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                {otvorena ? (
                  <TableRow className="bg-background-alt/40 hover:bg-background-alt/40">
                    <TableCell colSpan={5} className="px-3 py-1.5 text-xs">
                      <ul className="space-y-0.5" aria-label={`Kde je — ${p.popis}`}>
                        {zostavy.map((z) => (
                          <li key={z.zostava}>
                            <span className="font-medium">{nazovZostavy(z)}</span>
                            <span className="text-muted-foreground">
                              {" — "}
                              {z.dielce || `${z.kusov} ks`}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      <VyberPolozkyDialog
        polozka={editovana}
        stav={klucEditovanej ? (stavy[klucEditovanej] ?? PRAZDNY_STAV) : PRAZDNY_STAV}
        filter={editovana ? filterPre(editovana) : { druh: "plosny" }}
        kontext={editovana ? vetaKontextu(editovana, menaZostav) : null}
        menaZostav={menaZostav}
        predvyplnene={
          editovana && predvyplneniePre ? predvyplneniePre(editovana) : undefined
        }
        onZmena={(s, nazov) => {
          if (klucEditovanej) onZmena(klucEditovanej, s, nazov);
        }}
        onKovanie={
          onKovanie && editovana
            ? () => {
                onKovanie(editovana);
              }
            : undefined
        }
        onClose={() => setEditovana(null)}
      />
    </section>
  );
}

/** Čo sa na položku reálne priradí — alebo že sa nepriradilo nič. */
function PriradenieText(props: {
  polozka: MapovaniePolozka;
  stav: StavPolozky;
  nazov: string | undefined;
}): JSX.Element {
  const ina = inaHrubkaRiadku(props.polozka, props.stav.zmazat ? null : props.stav.material_id);
  return (
    <>
      <PriradenieHodnota {...props} />
      {ina ? (
        // Dielce dostanú hrúbku materiálu, nie z TopSolidu (KOLO 217)
        <Badge
          variant="outline"
          className="shrink-0 border-amber-400 text-amber-700"
          title={`V TopSolide ${formatMm(ina.zRiadku)} mm — dielce dostanú hrúbku materiálu. Ak to tak nemá byť, oprav model v TopSolide.`}
        >
          dielce {zmenaHrubky(ina.zRiadku, ina.zMaterialu)}
        </Badge>
      ) : null}
    </>
  );
}

function PriradenieHodnota({
  polozka,
  stav,
  nazov,
}: {
  polozka: MapovaniePolozka;
  stav: StavPolozky;
  nazov: string | undefined;
}): JSX.Element {
  if (stav.zmazat) {
    return (
      <>
        <span className="text-muted-foreground">nenahráva sa</span>
        {jeZapamatane(polozka, stav) ? <ZapamataneStitok /> : null}
      </>
    );
  }
  if (stav.material_id && jeZapamatane(polozka, stav)) {
    // Hodnota z pamäte priamo v riadku — bez kliknutia (KOLO 204)
    return (
      <>
        <span className="truncate">
          <span className="font-mono">{polozka.naucene_kod || polozka.naucene_nazov}</span>
          {polozka.naucene_kod && polozka.naucene_nazov ? (
            <span className="text-muted-foreground"> — {polozka.naucene_nazov}</span>
          ) : null}
        </span>
        <ZapamataneStitok />
      </>
    );
  }
  if (jeZoZakazky(polozka, stav)) {
    return (
      <>
        <span className="truncate">
          <span className="font-mono">{polozka.zakazka_kod || polozka.zakazka_nazov}</span>
          {polozka.zakazka_kod && polozka.zakazka_nazov ? (
            <span className="text-muted-foreground"> — {polozka.zakazka_nazov}</span>
          ) : null}
        </span>
        <ZoZakazkyStitok />
      </>
    );
  }
  if (stav.material_id) {
    return (
      <>
        <span className="truncate font-mono">{nazov ?? "vybraté ručne"}</span>
        <Badge variant="secondary">ručne</Badge>
      </>
    );
  }
  if (polozka.auto_material_id) {
    return (
      <>
        <span className="truncate font-mono">
          {polozka.auto_kod || polozka.auto_nazov}
        </span>
        {polozka.auto_rozmer ? (
          <span className="text-muted-foreground">
            ({polozka.auto_rozmer})
          </span>
        ) : null}
      </>
    );
  }
  // Prázdna bunka by nepovedala, že sa tu má rozhodnúť
  return (
    <span className="italic text-destructive">— vyber materiál</span>
  );
}
